import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { loadStripe } from '@stripe/stripe-js';

// Stripe API key - using Klorah's live key for sessions
const STRIPE_KEY = "pk_live_51IuUXbLwpaCHfpZ1EZNNYaQYdXSIrE6NrdA3r7lUQOIq5tsdhC7MaTITIMFb3nW4yxqyppRd8C66QcxWhuoPC8aa00Ow2q0o0z";

const StripeContext = createContext();

export function StripeProvider({ children }) {
  const [userId, setUserId] = useState(null);
  const [stripeInstance, setStripeInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const auth = getAuth();
  
  // Initialize Stripe when the context loads
  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const stripe = await loadStripe(STRIPE_KEY);
        setStripeInstance(stripe);
      } catch (error) {
        console.error('Error creating Stripe instance:', error);
      }
    };

    initializeStripe();
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  // Function to get the Stripe instance
  const getStripe = () => {
    if (!stripeInstance) {
      return loadStripe(STRIPE_KEY)
        .then(newStripe => {
          setStripeInstance(newStripe);
          return newStripe;
        })
        .catch(error => {
          console.error('Error creating Stripe instance in getStripe:', error);
          return null;
        });
    }
    
    return Promise.resolve(stripeInstance);
  };

  // Function to create a checkout session for subscription
  const createSubscriptionCheckout = async (planType = 'yearly') => {
    if (!userId) throw new Error('User must be logged in to subscribe');
    
    try {
      const response = await fetch('https://klorah-fast-server.onrender.com/api/sessions/subscription/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'instructor-id': userId,
        },
        body: JSON.stringify({ 
          planType: planType // 'monthly' or 'yearly'
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'An error occurred during checkout creation');
      }
      
      if (!data.checkoutUrl) {
        throw new Error('No checkout URL returned from the server');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.checkoutUrl;
      
      return data;
    } catch (error) {
      console.error('Detailed checkout error:', error);
      throw new Error(error.message || 'Error creating checkout session. Please try again later.');
    }
  };

  // Function to handle checkout redirects
  const redirectToCheckout = async (sessionId) => {
    try {
      const stripe = await getStripe();
      
      if (!stripe) {
        throw new Error('Failed to initialize Stripe');
      }
      
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        throw new Error(error.message || 'Failed to redirect to checkout');
      }
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  };

  // Function to create customer if not exists
  const ensureCustomer = async (email) => {
    if (!userId) throw new Error('User must be logged in');
    
    try {
      const response = await fetch('https://klorah-fast-server.onrender.com/api/sessions/create-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          userId: userId,
          email: email
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create customer');
      }
      
      return data.customer_id;
    } catch (error) {
      console.error('Error ensuring customer:', error);
      throw error;
    }
  };

  const value = {
    userId,
    isLoading,
    stripeInstance,
    getStripe,
    createSubscriptionCheckout,
    redirectToCheckout,
    ensureCustomer
  };

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  );
}

export function useStripe() {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
} 